import { useState } from 'react'
import { AppDialog, type AppDialogAction } from '../Common/AppDialog'
import { useNls } from '../../nls/nlsProvider'

type DialogActionResult = void | Promise<void>

interface ConfirmDialogProps {
  title: string
  body: string
  confirmLabel?: string
  cancelLabel?: string
  tone?: 'danger' | 'neutral'
  onConfirm: () => DialogActionResult
  onCancel: () => DialogActionResult
  /** Optional middle action (e.g. "Discard all" alongside "Save all"/"Cancel"). */
  secondaryLabel?: string
  secondaryTone?: 'danger' | 'neutral'
  onSecondary?: () => DialogActionResult
}

/**
 * Inline modal shown over the Detailed View overlay. Used by every Discard
 * variant — per spec, all discards prompt for confirmation. When a
 * `secondaryLabel`/`onSecondary` pair is provided, a third button renders
 * between Cancel and Confirm (used by the leave-Manage-with-drafts dialog).
 */
export const ConfirmDialog = ({
  title,
  body,
  confirmLabel,
  cancelLabel,
  tone,
  onConfirm,
  onCancel,
  secondaryLabel,
  secondaryTone,
  onSecondary,
}: ConfirmDialogProps) => {
  const { t } = useNls()
  const [pendingAction, setPendingAction] = useState<'cancel' | 'secondary' | 'confirm' | null>(null)
  const busy = pendingAction !== null
  const resolvedConfirmLabel = confirmLabel ?? t('NLSManageActionYesSure')
  const resolvedCancelLabel = cancelLabel ?? t('NLSCommonActionCancel')

  const runAction = (kind: 'cancel' | 'secondary' | 'confirm', action?: () => DialogActionResult) => {
    if (busy || !action) return
    const result = action()
    if (result && typeof result.then === 'function') {
      setPendingAction(kind)
      void result.then(() => {
        setPendingAction((current) => (current === kind ? null : current))
      }, () => {
        setPendingAction((current) => (current === kind ? null : current))
      })
    }
  }

  const actions: AppDialogAction[] = [
    {
      label: resolvedCancelLabel,
      variant: 'ghost',
      disabled: busy,
      loading: pendingAction === 'cancel',
      onClick: () => runAction('cancel', onCancel),
    },
    ...(secondaryLabel && onSecondary ? [
      {
        label: secondaryLabel,
        variant: secondaryTone === 'danger' ? ('danger' as const) : ('secondary' as const),
        disabled: busy,
        loading: pendingAction === 'secondary',
        onClick: () => runAction('secondary', onSecondary),
      },
    ] : []),
    {
      label: resolvedConfirmLabel,
      variant: tone === 'danger' ? ('danger' as const) : ('primary' as const),
      disabled: busy,
      loading: pendingAction === 'confirm',
      autoFocus: true,
      onClick: () => runAction('confirm', onConfirm),
    },
  ]

  return (
    <AppDialog
      open={true}
      size="sm"
      title={title}
      message={body}
      tone={tone === 'danger' ? 'danger' : undefined}
      onDismiss={() => runAction('cancel', onCancel)}
      actions={actions}
    />
  )
}
